import addUserSuggestionFragment from 'Root/App/Contexts/Authorizations/SlideContainer/AddUser/Suggestion/add-user-suggestion.graphql'
import fetchElementAuthorizationsAuthorizationFragment from 'Root/App/Contexts/Authorizations/SlideContainer/Row/Authorization/fetch-element-authorizations-authorization.graphql'
import fetchElementAuthorizationsInviteFragment from 'Root/App/Contexts/Authorizations/SlideContainer/Row/Invite/fetch-element-authorizations-invite.graphql'
import userProfileFragment from 'Root/App/Item/UserProfile/user-profile.graphql'

import { useEffect, useState } from 'react'

import { filter, map } from 'lodash'
import InfiniteScroll from 'react-infinite-scroll-component'

import { gql, useQuery } from '@apollo/client'
import { Trans } from '@lingui/macro'
import Title from 'Root/App/Title'
import FetchMoreLoading from 'Root/Pages/Homepage/App/FetchMoreLoading'
import { useCurrentUserId } from 'lib/use-current-user-id'

import AddUser from './AddUser'
import Loading from './Loading'
import Authorization from './Row/Authorization'
import Invite from './Row/Invite'
import ShareSectionHeading from './share-section-heading'

const ITEMS_PER_PAGE = 25

const GET_ELEMENT_AUTHORIZATION_FOR_USERS = gql`
  ${userProfileFragment}
  ${addUserSuggestionFragment}
  ${fetchElementAuthorizationsAuthorizationFragment}
  ${fetchElementAuthorizationsInviteFragment}
  fragment PaginatedAuthorizationsFields on PaginatedAuthorizationsAndInvites {
    data {
      __typename
      ... on Authorization {
        ...FetchElementAuthorizationsAuthorization
      }
      ... on Invite {
        ...FetchElementAuthorizationsInvite
      }
    }
    paginatorInfo {
      currentPage
      lastPage
      perPage
      total
      count
      hasMorePages
    }
  }

  query GetElementAuthorizationForUsers(
    $id: ID!
    $page: Int = 1
    $first: Int = 25
  ) {
    user: me {
      emails {
        email
        id
        primary
        verifiedAt
      }
      id
    }

    element(id: $id) {
      __typename

      ... on Collection {
        id
        paginatedAuthorizations: paginatedAuthorizationsAndInvites(
          page: $page
          first: $first
        ) {
          ...PaginatedAuthorizationsFields
        }
        creator {
          ...UserProfile
          email
        }
      }

      ... on Content {
        id
        paginatedAuthorizations: paginatedAuthorizationsAndInvites(
          page: $page
          first: $first
        ) {
          ...PaginatedAuthorizationsFields
        }
        creator {
          ...UserProfile
          email
        }
      }

      ... on Folder {
        id
        paginatedAuthorizations: paginatedAuthorizationsAndInvites(
          page: $page
          first: $first
        ) {
          ...PaginatedAuthorizationsFields
        }
        creator {
          ...UserProfile
          email
        }
      }
    }

    knownUsers {
      ...AddUserSuggestion
    }
  }
`

const ShareToUser = ({ id }) => {
  const userId = useCurrentUserId()
  const [allItems, setAllItems] = useState<any[]>([])
  const [currentPage, setCurrentPage] = useState(1)
  const [hasMoreData, setHasMoreData] = useState(true)
  const { data, loading, fetchMore } = useQuery(
    GET_ELEMENT_AUTHORIZATION_FOR_USERS,
    {
      variables: { id, page: 1, first: ITEMS_PER_PAGE },
      fetchPolicy: 'network-only',
    }
  )

  useEffect(() => {
    if (
      loading ||
      currentPage !== 1 ||
      !data ||
      !data.element ||
      !data.element.paginatedAuthorizations
    )
      return

    const { data: pageData, paginatorInfo } =
      data.element.paginatedAuthorizations
    const filteredItems = filter(
      pageData,
      ({ __typename, ...item }) =>
        __typename === 'Invite' || !('user' in item) || item.user.id !== userId
    )
    setAllItems(filteredItems)
    setHasMoreData(paginatorInfo.hasMorePages)
  }, [loading, data, currentPage, userId])

  const loadMore = async () => {
    if (!hasMoreData) return
    const nextPage = currentPage + 1
    const result = await fetchMore({
      variables: { id, page: nextPage, first: ITEMS_PER_PAGE },
    })
    const conn = result.data?.element?.paginatedAuthorizations
    if (conn) {
      setAllItems((prev) => [...prev, ...conn.data])
      setHasMoreData(conn.paginatorInfo.hasMorePages)
      setCurrentPage(nextPage)
    }
  }

  if (loading && allItems.length === 0) {
    return <Loading />
  }

  if (!data || !data.element) {
    return null
  }

  const element = data.element

  return (
    <span id="share-by-email" className="flex flex-col gap-4">
      <ShareSectionHeading
        title={<Trans>Condividi tramite email</Trans>}
        subtitle={
          <Trans>
            Manderemo alle persone che inviti un link magico e le istruzioni per
            accedere.
          </Trans>
        }
      />
      <AddUser
        id={element.id}
        knownUsers={data.knownUsers}
        creator={element.creator}
        currentUserData={data.user.emails}
      />

      {allItems.length > 0 && (
        <Title size="XS">
          <Trans>Condiviso con</Trans>:
          <hr className="mt-4 divide-gray-200" />
        </Title>
      )}

      <InfiniteScroll
        dataLength={allItems.length}
        next={loadMore}
        hasMore={hasMoreData}
        loader={<FetchMoreLoading />}
        scrollableTarget="slide-container"
      >
        <div role="list" className="flex flex-col divide-y divide-gray-200">
          {map(allItems, (item) =>
            item.__typename === 'Authorization' ? (
              <Authorization
                authorization={item}
                elementId={element.id}
                key={item.id}
              />
            ) : (
              <Invite elementId={element.id} invite={item} key={item.id} />
            )
          )}
        </div>
      </InfiniteScroll>

      {!hasMoreData && allItems.length > 0 && (
        <div className="text-center text-gray-500 mt-5 pb-4">
          <Trans>Tutti i risultati sono stati caricati</Trans>
        </div>
      )}
    </span>
  )
}

export default ShareToUser
